import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Icon from '../Icon';
import useDismiss from '@hooks/useDismiss';
import { getRovingTargetIndex, ROVING_KEY, ROVING_ORIENTATION } from '@hooks/useRovingFocus';
import { PICKER_PANEL_PLACEMENT, PICKER_PANEL_PLACEMENTS, PICKER_PANEL_SEARCH_ICON } from './constants';
import { filterPickerItems } from './helpers';

// Design System — PickerPanel
// A small floating panel that picks one thing from a short list: a search row, the matching
// items, and an optional action at the foot ("Create new step"). Uncontrolled search by
// default — pass `query` and `onQueryChange` to own it, e.g. when the list comes from a
// request. It reports a pick and an outside click (a click on `anchorRef`, the control that
// toggles the panel, is not one); the caller decides what either means and where the panel sits. Focus stays in the search field (a combobox): ArrowUp/Down and
// Home/End move the active option, Enter picks it.
// Styles: ./index.less ships via less/ats_style.less (white-label pipeline).

const PickerPanel = ({
    items = [],
    onSelect,
    onDismiss,
    anchorRef,
    searchPlaceholder,
    query,
    onQueryChange,
    emptyText,
    footer,
    placement = PICKER_PANEL_PLACEMENT.BOTTOM,
    autoFocus = true,
    className,
    listClassName,
    itemClassName,
    ref,
    ...rest
}) => {
    const panelRef = useRef(null);
    const baseId = useId();
    const listId = `${baseId}-list`;
    const optionId = (index) => `${baseId}-option-${index}`;
    const [ownQuery, setOwnQuery] = useState('');
    const [activeIndex, setActiveIndex] = useState(-1);
    const controlled = query != null;
    const term = controlled ? query : ownQuery;

    const setRefs = (node) => {
        panelRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
    };

    useDismiss(panelRef, onDismiss, { enabled: typeof onDismiss === 'function', ignore: anchorRef ? [anchorRef] : [] });

    const shown = useMemo(() => filterPickerItems(items, term), [items, term]);
    const active = activeIndex < shown.length ? activeIndex : -1;

    useEffect(() => {
        if (active >= 0) document.getElementById(optionId(active))?.scrollIntoView?.({ block: 'nearest' });
    }, [active]);

    const pick = (item) => !item.disabled && onSelect?.(item);

    const onSearchKeyDown = (event) => {
        if (event.key === ROVING_KEY.ENTER) {
            if (active < 0) return;
            event.preventDefault();
            pick(shown[active]);
            return;
        }
        const fromNowhere = { [ROVING_KEY.ARROW_DOWN]: ROVING_KEY.HOME, [ROVING_KEY.ARROW_UP]: ROVING_KEY.END };
        const target = getRovingTargetIndex({
            key: active < 0 ? (fromNowhere[event.key] ?? event.key) : event.key,
            index: active,
            count: shown.length,
            orientation: ROVING_ORIENTATION.VERTICAL,
            isDisabled: (index) => Boolean(shown[index]?.disabled),
        });
        if (target === null) return;
        event.preventDefault();
        if (target >= 0) setActiveIndex(target);
    };

    return (
        <div
            ref={setRefs}
            className={classNames(
                'ds-picker-panel',
                `ds-picker-panel--${PICKER_PANEL_PLACEMENTS.includes(placement) ? placement : PICKER_PANEL_PLACEMENT.BOTTOM}`,
                className,
            )}
            {...rest}
        >
            <div className="ds-picker-panel__search">
                <Icon value={PICKER_PANEL_SEARCH_ICON} noMargin />
                <input
                    type="text"
                    className="ds-picker-panel__search-input search-input"
                    placeholder={searchPlaceholder}
                    aria-label={searchPlaceholder}
                    role="combobox"
                    aria-expanded="true"
                    aria-controls={listId}
                    aria-autocomplete="list"
                    aria-activedescendant={active >= 0 ? optionId(active) : undefined}
                    value={term}
                    autoFocus={autoFocus}
                    onKeyDown={onSearchKeyDown}
                    onChange={(event) => {
                        const next = event.target.value;
                        setActiveIndex(-1);
                        if (!controlled) setOwnQuery(next);
                        onQueryChange?.(next);
                    }}
                />
            </div>
            <ul
                id={listId}
                role="listbox"
                aria-label={searchPlaceholder}
                className={classNames('ds-picker-panel__list', listClassName)}
            >
                {shown.map((item, index) => (
                    <li
                        key={item.id ?? item.label}
                        id={optionId(index)}
                        role="option"
                        aria-selected={index === active}
                        className={classNames(
                            'ds-picker-panel__item',
                            {
                                'ds-picker-panel__item--active': index === active,
                                'ds-picker-panel__item--disabled': item.disabled,
                                disabled: item.disabled,
                            },
                            itemClassName,
                            item.className,
                        )}
                        data-text={item.label}
                        aria-disabled={item.disabled || undefined}
                        onClick={() => pick(item)}
                    >
                        {item.icon && <Icon value={item.icon} noMargin className="ds-picker-panel__item-icon" />}
                        <span className="ds-picker-panel__item-label">{item.label}</span>
                    </li>
                ))}
                {shown.length === 0 && emptyText && (
                    <li role="presentation" className="ds-picker-panel__empty">{emptyText}</li>
                )}
            </ul>
            {footer && <div className="ds-picker-panel__footer">{footer}</div>}
        </div>
    );
};

PickerPanel.propTypes = {
    /** The choices: `{ id, label, icon, disabled, className }` */
    items: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            label: PropTypes.string,
            icon: PropTypes.string,
            disabled: PropTypes.bool,
            className: PropTypes.string,
        }),
    ),
    /** Called with the chosen item */
    onSelect: PropTypes.func,
    /** Called on Escape and on a mousedown/touch outside the panel */
    onDismiss: PropTypes.func,
    /** The control that toggles the panel; clicks on it never dismiss, so its own click decides */
    anchorRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
    /** Translated placeholder, also the search field's accessible name */
    searchPlaceholder: PropTypes.string,
    /** Controlled search text; omit it and the panel keeps its own */
    query: PropTypes.string,
    onQueryChange: PropTypes.func,
    /** Shown when nothing matches */
    emptyText: PropTypes.node,
    /** An action under the list, e.g. "Create new step" */
    footer: PropTypes.node,
    /** Which side of its anchor the panel opens on */
    placement: PropTypes.oneOf(PICKER_PANEL_PLACEMENTS),
    /** Focuses the search field on mount */
    autoFocus: PropTypes.bool,
    className: PropTypes.string,
    /** Extra class on the list, for call sites whose selectors name it */
    listClassName: PropTypes.string,
    /** Extra class on every item */
    itemClassName: PropTypes.string,
    ref: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
};

export { PICKER_PANEL_PLACEMENT, PICKER_PANEL_PLACEMENTS };
export { filterPickerItems };
export default PickerPanel;
